import type { SpotifyPlayer, SpotifyPlaybackState } from '../types/spotify';
import { createApiClient, type ApiClient } from './ApiClient';
import type {
  SpotifyTrack,
  SpotifyUser,
  SpotifyUserResponse,
  SpotifySearchResponse,
  SpotifyAlbumTracksResponse,
  SpotifyArtistTopTracksResponse,
  SpotifyTokenResponse,
  SpotifyPlayResponse,
  SpotifyStatusResponse,
  SpotifyLogoutResponse,
} from '@maso-dj/shared-types';
import { logger } from '@maso-dj/shared-types';

// Re-export types for backward compatibility
export type { SpotifyTrack, SpotifyUser } from '@maso-dj/shared-types';

export class SpotifyService {
  private apiUrl: string;
  private apiClient: ApiClient;
  private player: SpotifyPlayer | null = null;
  private deviceId: string | null = null;
  private onPlayerReadyCallback: (() => void) | null = null;
  private onPlaybackStateCallback: ((state: SpotifyPlaybackState | null) => void) | null = null;
  private isInitializing: boolean = false;
  private initializationPromise: Promise<void> | null = null;
  private fadeOutInterval: number | null = null;
  private fadeInInterval: number | null = null;
  private baseVolume: number = 1.0; // Default 100% volume
  private currentVolume: number = 1.0; // Track current volume during fadeout

  // End time monitoring
  private endTimeCheckInterval: number | null = null;
  private currentSongEndTime: number | null = null;

  constructor() {
    // In production, use relative URLs. In development, use the dev server port.
    const isDev = import.meta.env.DEV;
    this.apiUrl = isDev
      ? `https://${window.location.hostname}:3002`
      : '';

    this.apiClient = createApiClient(this.apiUrl);
    logger.info('SpotifyService initialized with API URL:', this.apiUrl);
  }

  /**
   * Check if user is authenticated with Spotify
   */
  async isAuthenticated(): Promise<boolean> {
    const response = await this.apiClient.get<SpotifyStatusResponse>(
      '/api/spotify/status',
      {
        credentials: 'include',
        description: 'Check Spotify authentication status',
        suppressErrorLog: true // Don't log errors for auth checks
      }
    );

    return 'authenticated' in response && response.authenticated === true;
  }

  /**
   * Get current user's Spotify profile
   */
  async getCurrentUser(): Promise<SpotifyUser | null> {
    const response = await this.apiClient.get<SpotifyUserResponse>(
      '/api/spotify/user',
      {
        credentials: 'include',
        description: 'Get Spotify user profile',
        suppressErrorLog: true
      }
    );

    return response.success ? response.user || null : null;
  }

  /**
   * Initiate Spotify OAuth flow
   * Uses full page redirect to preserve session
   */
  login(): void {
    // Redirect to Spotify OAuth
    window.location.href = `${this.apiUrl}/api/spotify/auth`;
  }

  /**
   * Logout from Spotify
   */
  async logout(): Promise<void> {
    const response = await this.apiClient.post<SpotifyLogoutResponse>(
      '/api/spotify/logout',
      undefined,
      {
        credentials: 'include',
        description: 'Logout from Spotify',
        suppressErrorLog: true
      }
    );

    if (!response.success) {
      logger.warn('Logout request failed:', response.httpStatus);
    }

    if (this.player) {
      this.player.disconnect();
      this.player = null;
      this.deviceId = null;
    }
  }

  /**
   * Search for tracks on Spotify
   */
  async searchTracks(query: string, limit: number = 20): Promise<SpotifyTrack[]> {
    const response = await this.apiClient.get<SpotifySearchResponse>(
      `/api/spotify/search?q=${encodeURIComponent(query)}&limit=${limit}`,
      {
        credentials: 'include',
        description: 'Search Spotify tracks'
      }
    );

    return response.success ? response.tracks || [] : [];
  }

  /**
   * Get all tracks from an album
   */
  async getAlbumTracks(albumId: string): Promise<{ tracks: SpotifyTrack[]; albumName: string; albumArtist: string }> {
    const response = await this.apiClient.get<SpotifyAlbumTracksResponse>(
      `/api/spotify/album/${albumId}`,
      {
        credentials: 'include',
        description: 'Get album tracks'
      }
    );

    return response.success
      ? {
          tracks: response.tracks || [],
          albumName: response.albumName || '',
          albumArtist: response.albumArtist || ''
        }
      : { tracks: [], albumName: '', albumArtist: '' };
  }

  /**
   * Get top tracks from an artist
   */
  async getArtistTopTracks(artistId: string): Promise<{ tracks: SpotifyTrack[]; artistName: string }> {
    const response = await this.apiClient.get<SpotifyArtistTopTracksResponse>(
      `/api/spotify/artist/${artistId}/top-tracks`,
      {
        credentials: 'include',
        description: 'Get artist top tracks'
      }
    );

    return response.success
      ? { tracks: response.tracks || [], artistName: response.artistName || '' }
      : { tracks: [], artistName: '' };
  }

  /**
   * Initialize Spotify Web Playback SDK
   */
  async initializePlayer(accessToken: string): Promise<void> {
    // Prevent duplicate initialization
    if (this.isInitializing) {
      logger.info('Player initialization already in progress, waiting...');
      if (this.initializationPromise) {
        return this.initializationPromise;
      }
      return;
    }

    if (this.player && this.deviceId) {
      logger.info('Spotify player already initialized with device ID:', this.deviceId);
      return;
    }

    logger.info('Initializing Spotify Web Playback SDK...');
    this.isInitializing = true;

    this.initializationPromise = new Promise((resolve, reject) => {
      // Load Spotify Web Playback SDK
      if (!window.Spotify) {
        logger.info('Loading Spotify SDK script...');
        const script = document.createElement('script');
        script.src = 'https://sdk.scdn.co/spotify-player.js';
        script.async = true;

        script.onerror = (): void => {
          logger.error('Failed to load Spotify SDK script');
          this.isInitializing = false;
          this.initializationPromise = null;
          reject(new Error('Failed to load Spotify SDK'));
        };

        document.body.appendChild(script);

        window.onSpotifyWebPlaybackSDKReady = (): void => {
          logger.info('Spotify SDK ready callback fired');
          this.createPlayer(accessToken, resolve, reject);
        };
      } else {
        logger.info('Spotify SDK already loaded');
        this.createPlayer(accessToken, resolve, reject);
      }
    });

    try {
      await this.initializationPromise;
      this.isInitializing = false;
    } catch (error) {
      this.isInitializing = false;
      this.initializationPromise = null;
      throw error;
    }
  }

  private createPlayer(
    accessToken: string,
    resolve: () => void,
    reject: (error: Error) => void
  ): void {
    logger.info('Creating Spotify player...');

    if (!window.Spotify) {
      logger.error('Spotify SDK not available');
      reject(new Error('Spotify SDK not loaded'));
      return;
    }

    logger.info('Creating Spotify.Player instance with name: MasoDJ');

    try {
      let isResolved = false;

      // Set a timeout to reject if player doesn't become ready
      const timeout = setTimeout(() => {
        if (!isResolved) {
          logger.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          logger.error('🔴 SPOTIFY PLAYER INITIALIZATION TIMEOUT (15 seconds)');
          logger.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          logger.error('The player was created but never became ready.');
          logger.error('This usually means Widevine DRM failed to initialize.');
          logger.error('');
          logger.error('TROUBLESHOOTING:');
          logger.error('1. Check if you have Spotify Premium');
          logger.error('2. Try accessing via https://localhost:3001 instead of 127.0.0.1');
          logger.error('3. Check browser DRM settings (chrome://settings/content/protectedContent)');
          logger.error('4. Clear browser cache and restart browser');
          logger.error('5. Check browser console for initialization_error or account_error messages above');
          logger.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          reject(new Error('Spotify player initialization timeout. Please check your Spotify Premium status and try again.'));
        }
      }, 15000); // Increased to 15 seconds

      this.player = new window.Spotify.Player({
        name: 'MasoDJ',
        getOAuthToken: (cb: (token: string) => void): void => {
          logger.debug('Spotify SDK requesting OAuth token');
          cb(accessToken);
        },
        volume: this.baseVolume
      });

      logger.info('Spotify.Player instance created, adding listeners...');

      // Error handling
      this.player.addListener('initialization_error', ({ message }: { message: string }): void => {
        logger.error('🔴 Spotify initialization_error:', message);

        // Check if this is a DRM/EME error (indicates non-Premium account OR certificate issue)
        if (message.includes('Failed to initialize player')) {
          logger.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          logger.error('🔴 SPOTIFY PLAYER DRM/EME INITIALIZATION ERROR');
          logger.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          logger.error('This is usually caused by ONE of these issues:');
          logger.error('');
          logger.error('1. BRAVE BROWSER with Widevine disabled (MOST COMMON)');
          logger.error('   → Open brave://settings/extensions');
          logger.error('   → Find "Google Widevine" and toggle it ON');
          logger.error('   → Reload this page');
          logger.error('   → Or just use Chrome/Edge (Widevine enabled by default)');
          logger.error('');
          logger.error('2. NON-PREMIUM SPOTIFY ACCOUNT');
          logger.error('   → The Spotify Web Playback SDK REQUIRES Spotify Premium');
          logger.error('   → Check your account at: https://www.spotify.com/account/');
          logger.error('');
          logger.error('3. BROWSER DRM DISABLED');
          logger.error('   → Chrome: chrome://settings/content/protectedContent');
          logger.error('   → Firefox: about:preferences#privacy → Play DRM content');
          logger.error('   → Ensure "Allow sites to play protected content" is ON');
          logger.error('');
          logger.error('SIMPLEST FIX: Just use Chrome or Edge for Spotify playback');
          logger.error('Current Browser: ' + navigator.userAgent);
          logger.error('Error message: ' + message);
          logger.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

          // Reject with detailed error after a short delay if not resolved
          setTimeout(() => {
            if (!isResolved) {
              isResolved = true;
              clearTimeout(timeout);
              reject(new Error('Spotify Player failed to initialize. This is usually caused by: (1) Non-Premium Spotify account, or (2) Widevine DRM certificate trust issue. Try accessing via https://localhost:3001 instead of 127.0.0.1, or check browser DRM settings.'));
            }
          }, 2000);
        } else {
          logger.error('Possible causes:');
          logger.error('1. Non-Premium Spotify account');
          logger.error('2. Browser not supported');
          logger.error('3. Invalid access token or missing scopes');
        }
        // Don't reject immediately - player may retry and succeed
      });

      this.player.addListener('authentication_error', ({ message }: { message: string }): void => {
        logger.error('Spotify authentication error:', message);
        if (!isResolved) {
          isResolved = true;
          clearTimeout(timeout);
          reject(new Error(`Spotify authentication failed: ${message}`));
        }
      });

      this.player.addListener('account_error', ({ message }: { message: string }): void => {
        logger.error('Spotify account error:', message);
        logger.error('This usually means you need a Spotify Premium account');
        if (!isResolved) {
          isResolved = true;
          clearTimeout(timeout);
          reject(new Error(`Spotify account error: ${message}. Web Playback SDK requires Spotify Premium.`));
        }
      });

      this.player.addListener('playback_error', ({ message }: { message: string }): void => {
        // "Cannot perform operation; no list was loaded" is a harmless warning during device activation
        if (message.includes('no list was loaded')) {
          logger.warn('Spotify playback warning (device activation):', message);
        } else {
          logger.error('Spotify playback error:', message);
        }
      });

      // Ready
      this.player.addListener('ready', ({ device_id }: { device_id: string }): void => {
        logger.info('Spotify player ready with device ID:', device_id);
        this.deviceId = device_id;
        if (!isResolved) {
          isResolved = true;
          clearTimeout(timeout);
          logger.info('Player initialization complete');
          if (this.onPlayerReadyCallback) {
            this.onPlayerReadyCallback();
          }
          resolve();
        }
      });

      // Not Ready
      this.player.addListener('not_ready', ({ device_id }: { device_id: string }): void => {
        logger.info('Spotify player not ready with device ID:', device_id);
      });

      // Player state changed
      this.player.addListener('player_state_changed', (state: SpotifyPlaybackState | null) => {
        if (this.onPlaybackStateCallback) {
          this.onPlaybackStateCallback(state);
        }
      });

      logger.info('All listeners added, connecting player...');

      // Connect to player
      this.player.connect().then((success: boolean) => {
        if (success) {
          logger.info('Spotify player connected successfully');
        } else {
          logger.error('Spotify player connection failed');
          if (!isResolved) {
            isResolved = true;
            clearTimeout(timeout);
            reject(new Error('Failed to connect Spotify player'));
          }
        }
      });
    } catch (error) {
      logger.error('Error creating Spotify player:', error);
      reject(error instanceof Error ? error : new Error('Unknown error creating player'));
    }
  }

  /**
   * Get access token from backend
   */
  async getAccessToken(): Promise<string | null> {
    const response = await this.apiClient.get<SpotifyTokenResponse>(
      '/api/spotify/token',
      {
        credentials: 'include',
        description: 'Get Spotify access token',
        suppressErrorLog: true
      }
    );

    return response.success ? response.accessToken || null : null;
  }

  /**
   * Play a track
   */
  async play(uri: string, startTime?: number, endTime?: number): Promise<void> {
    logger.debug('SpotifyService.play() called', {
      uri,
      deviceId: this.deviceId,
      hasPlayer: !!this.player,
      startTime,
      endTime
    });

    if (!this.deviceId) {
      logger.error('Cannot play: Spotify player not ready. Device ID is null.');
      throw new Error('Spotify player not ready. Please wait for player initialization.');
    }

    logger.debug('Sending play request to server...');
    const response = await this.apiClient.post<SpotifyPlayResponse>(
      '/api/spotify/play',
      {
        uri,
        deviceId: this.deviceId,
        position_ms: startTime !== undefined ? Math.floor(startTime * 1000) : undefined
      },
      {
        credentials: 'include',
        description: 'Play Spotify track'
      }
    );

    logger.debug('Play request response status:', response.httpStatus);

    if (!response.success) {
      const errorMsg = response.message || 'Failed to play track';
      logger.error('Play request failed:', errorMsg);
      throw new Error(errorMsg);
    }

    logger.info('Spotify track play request successful:', uri);

    // Start monitoring end time if specified
    if (endTime !== undefined) {
      this.startEndTimeMonitoring(endTime);
    }
  }

  /**
   * Pause playback
   */
  async pause(): Promise<void> {
    // Clear any fadeout in progress
    if (this.fadeOutInterval) {
      clearInterval(this.fadeOutInterval);
      this.fadeOutInterval = null;
    }

    if (this.player) {
      await this.player.pause();
    }
  }

  /**
   * Resume playback
   */
  async resume(): Promise<void> {
    if (this.player) {
      await this.player.resume();
    }
  }

  /**
   * Toggle play/pause
   */
  async togglePlay(): Promise<void> {
    if (this.player) {
      await this.player.togglePlay();
    }
  }

  /**
   * Set volume (0.0 to 1.0)
   */
  async setVolume(volume: number): Promise<void> {
    this.baseVolume = Math.max(0, Math.min(1, volume));
    if (this.player && !this.isFading()) {
      await this.player.setVolume(this.baseVolume);
    }
  }

  /**
   * Get current volume
   */
  getVolume(): number {
    return this.baseVolume;
  }

  /**
   * Fade out and pause playback
   */
  async fadeOutAndStop(fadeoutDuration: number, callback?: () => void, progressCallback?: (progress: number) => void): Promise<void> {
    // If fadeout duration is 0, pause immediately
    if (fadeoutDuration === 0) {
      await this.pause();
      if (callback) {
        callback();
      }
      return;
    }

    const fadeDuration = fadeoutDuration * 1000; // Convert to milliseconds
    const fadeSteps = 50;
    const stepDuration = fadeDuration / fadeSteps;
    const volumeStep = this.baseVolume / fadeSteps;
    let currentStep = 0;

    // Clear any existing fade intervals
    if (this.fadeOutInterval) {
      clearInterval(this.fadeOutInterval);
    }
    if (this.fadeInInterval) {
      clearInterval(this.fadeInInterval);
      this.fadeInInterval = null;
    }

    this.fadeOutInterval = window.setInterval(async () => {
      currentStep++;
      const newVolume = Math.max(0, this.baseVolume - (volumeStep * currentStep));
      this.currentVolume = newVolume; // Track current volume

      try {
        if (this.player) {
          await this.player.setVolume(newVolume);
        }
      } catch (error) {
        logger.error('Error setting volume during fadeout:', error);
      }

      // Report progress (0 to 1)
      if (progressCallback) {
        progressCallback(currentStep / fadeSteps);
      }

      if (currentStep >= fadeSteps || newVolume <= 0) {
        if (this.fadeOutInterval) {
          clearInterval(this.fadeOutInterval);
          this.fadeOutInterval = null;
        }

        // Pause and restore base volume
        try {
          await this.pause();
          if (this.player) {
            await this.player.setVolume(this.baseVolume);
            this.currentVolume = this.baseVolume; // Reset to base volume
          }
        } catch (error) {
          logger.error('Error pausing/resetting volume after fadeout:', error);
        }

        if (callback) {
          callback();
        }
      }
    }, stepDuration);
  }

  /**
   * Check if currently fading out
   */
  isFading(): boolean {
    return this.fadeOutInterval !== null;
  }

  /**
   * Abort fadeout and restore volume
   */
  async abortFadeout(fadeInDuration?: number, progressCallback?: (progress: number) => void, callback?: () => void): Promise<void> {
    if (!this.fadeOutInterval) {
      return; // No fadeout in progress
    }

    // Stop the fadeout
    clearInterval(this.fadeOutInterval);
    this.fadeOutInterval = null;

    // Get current volume (we track it during fadeout, default to 0 if unknown)
    const currentVolume = this.currentVolume ?? 0;
    const volumeDiff = this.baseVolume - currentVolume;

    if (volumeDiff <= 0) {
      // Already at or above base volume
      if (this.player) {
        await this.player.setVolume(this.baseVolume);
        this.currentVolume = this.baseVolume;
      }
      if (callback) {
        callback();
      }
      return;
    }

    // Use half the fadeout duration if not specified (we'll use a default of 5 seconds / 2 = 2.5 seconds)
    const fadeInTime = fadeInDuration ?? 2.5;
    const fadeDuration = fadeInTime * 1000; // Convert to milliseconds
    const fadeSteps = 50;
    const stepDuration = fadeDuration / fadeSteps;
    const volumeStep = volumeDiff / fadeSteps;
    let currentStep = 0;

    this.fadeInInterval = window.setInterval(async () => {
      currentStep++;
      const newVolume = Math.min(this.baseVolume, currentVolume + (volumeStep * currentStep));
      this.currentVolume = newVolume; // Track current volume

      try {
        if (this.player) {
          await this.player.setVolume(newVolume);
        }
      } catch (error) {
        logger.error('Error setting volume during fade-in:', error);
      }

      // Report progress (1 to 0, where 0 is fully restored)
      if (progressCallback) {
        progressCallback(1 - (currentStep / fadeSteps));
      }

      if (currentStep >= fadeSteps || newVolume >= this.baseVolume) {
        if (this.fadeInInterval) {
          clearInterval(this.fadeInInterval);
          this.fadeInInterval = null;
        }
        try {
          if (this.player) {
            await this.player.setVolume(this.baseVolume);
            this.currentVolume = this.baseVolume; // Reset to base volume
          }
        } catch (error) {
          logger.error('Error restoring volume after fade-in:', error);
        }
        if (callback) {
          callback();
        }
      }
    }, stepDuration);
  }

  /**
   * Get current playback state
   */
  async getCurrentState(): Promise<SpotifyPlaybackState | null> {
    if (this.player) {
      return await this.player.getCurrentState();
    }
    return null;
  }

  /**
   * Register callback for when player is ready
   */
  onPlayerReady(callback: () => void): void {
    this.onPlayerReadyCallback = callback;
  }

  /**
   * Register callback for playback state changes
   */
  onPlaybackState(callback: (state: SpotifyPlaybackState | null) => void): void {
    this.onPlaybackStateCallback = callback;
  }

  /**
   * Get device ID
   */
  getDeviceId(): string | null {
    return this.deviceId;
  }

  /**
   * Disconnect player
   */
  disconnect(): void {
    this.stopEndTimeMonitoring();
    if (this.player) {
      this.player.disconnect();
      this.player = null;
      this.deviceId = null;
    }
  }

  /**
   * Start monitoring for end time
   */
  private async startEndTimeMonitoring(endTime: number): Promise<void> {
    // Stop any existing monitoring
    this.stopEndTimeMonitoring();

    this.currentSongEndTime = endTime;

    // Check every 500ms if we've reached the end time
    this.endTimeCheckInterval = window.setInterval(async () => {
      if (!this.player) return;

      const state = await this.player.getCurrentState();
      if (!state) return;

      const currentTimeSeconds = state.position / 1000;

      if (this.currentSongEndTime !== null && currentTimeSeconds >= this.currentSongEndTime) {
        logger.debug('Reached Spotify song end time:', this.currentSongEndTime);
        this.stopEndTimeMonitoring();

        // Pause the player
        await this.pause();

        // Trigger playback state callback to notify that the song has ended
        if (this.onPlaybackStateCallback) {
          this.onPlaybackStateCallback({ ...state, paused: true, position: 0 });
        }
      }
    }, 500);
  }

  /**
   * Stop monitoring for end time
   */
  private stopEndTimeMonitoring(): void {
    if (this.endTimeCheckInterval) {
      clearInterval(this.endTimeCheckInterval);
      this.endTimeCheckInterval = null;
    }
    this.currentSongEndTime = null;
  }
}
